import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { usePageVisibility } from '@/hooks/usePageVisibility';

/** Morph themes: 3–4 complementary colors per orb; cycle every minute with smooth CSS transition */
type RGB = [number, number, number];
const MORPH_THEMES: RGB[][] = [
  [ [37, 99, 235], [34, 211, 238], [96, 165, 250], [6, 182, 212] ],   // Ocean: blue → cyan → light blue → cyan
  [ [139, 92, 246], [236, 72, 153], [217, 70, 239], [192, 132, 252] ], // Violet: purple → pink → magenta → lavender
  [ [6, 182, 212], [16, 185, 129], [59, 130, 246], [52, 211, 153] ],   // Teal shift: cyan → emerald → blue → mint
  [ [168, 85, 247], [244, 114, 182], [232, 121, 249], [139, 92, 246] ], // Berry: purple → pink → magenta → violet
  [ [59, 130, 246], [139, 92, 246], [34, 211, 238], [96, 165, 250] ],   // Cool wave: blue → purple → cyan → light blue
  [ [244, 114, 182], [52, 211, 153], [217, 70, 239], [236, 72, 153] ],  // Bloom: pink → mint → magenta → rose
];

/** Color changes every 1.5 minutes; orbs are static (no drift) for max energy savings */
const COLOR_UPDATE_INTERVAL_MS = 90_000;

/** Cycle orb count on all screens: 2 → 3 → 4 → 2 → 4 → 3 → 4 → 3 → 2, then repeat */
const ORB_COUNT_SEQUENCE: (2 | 3 | 4)[] = [2, 3, 4, 2, 4, 3, 4, 3, 2];
const ORB_COUNT_CYCLE_INTERVAL_MS = 40_000;

type OrbConfigItem = { size: number; top?: string; left?: string; right?: string; bottom?: string; paletteIndex: number };

// Orb sets: 2, 3, or 4 orbs per set for variety (selected by viewport)
const ORB_SET_2: OrbConfigItem[] = [
  { size: 380, top: '22%', left: '5%', paletteIndex: 0 },
  { size: 400, top: '35%', right: '5%', paletteIndex: 1 },
];

const ORB_SET_3: OrbConfigItem[] = [
  { size: 340, top: '20%', left: '8%', paletteIndex: 0 },
  { size: 360, top: '30%', right: '5%', paletteIndex: 1 },
  { size: 300, bottom: '15%', left: '38%', paletteIndex: 2 },
];

const ORB_SET_4: OrbConfigItem[] = [
  { size: 320, top: '22%', left: '10%', paletteIndex: 0 },
  { size: 300, top: '25%', right: '8%', paletteIndex: 1 },
  { size: 280, bottom: '20%', left: '15%', paletteIndex: 2 },
  { size: 300, bottom: '25%', right: '12%', paletteIndex: 3 },
];

// Mobile: same sets with smaller orbs (top % tuned so orbs aren't cut off by safe area)
const ORB_SET_2_MOBILE: OrbConfigItem[] = [
  { size: 220, top: '22%', left: '10%', paletteIndex: 0 },
  { size: 240, top: '35%', right: '5%', paletteIndex: 1 },
];

const ORB_SET_3_MOBILE: OrbConfigItem[] = [
  { size: 200, top: '20%', left: '8%', paletteIndex: 0 },
  { size: 220, top: '32%', right: '5%', paletteIndex: 1 },
  { size: 180, bottom: '18%', left: '35%', paletteIndex: 2 },
];

const ORB_SET_4_MOBILE: OrbConfigItem[] = [
  { size: 180, top: '20%', left: '8%', paletteIndex: 0 },
  { size: 170, top: '28%', right: '6%', paletteIndex: 1 },
  { size: 160, bottom: '22%', left: '12%', paletteIndex: 2 },
  { size: 170, bottom: '28%', right: '10%', paletteIndex: 3 },
];

// Reduced-motion: 2, 3, or 4 static orbs in corners
const REDUCED_MOTION_SET_2: OrbConfigItem[] = [
  { size: 360, top: '-8%', left: '-6%', paletteIndex: 0 },
  { size: 320, top: '-8%', right: '-10%', paletteIndex: 1 },
];

const REDUCED_MOTION_SET_3: OrbConfigItem[] = [
  { size: 360, top: '-8%', left: '-6%', paletteIndex: 0 },
  { size: 320, top: '8%', right: '-10%', paletteIndex: 1 },
  { size: 340, bottom: '-10%', left: '-4%', paletteIndex: 2 },
];

const REDUCED_MOTION_SET_4: OrbConfigItem[] = [
  { size: 360, top: '-8%', left: '-6%', paletteIndex: 0 },
  { size: 320, top: '8%', right: '-10%', paletteIndex: 1 },
  { size: 360, bottom: '-10%', left: '6%', paletteIndex: 2 },
  { size: 320, bottom: '-8%', right: '-6%', paletteIndex: 3 },
];

const REDUCED_MOTION_SET_2_MOBILE: OrbConfigItem[] = [
  { size: 240, top: '-10%', left: '-12%', paletteIndex: 0 },
  { size: 220, top: '-10%', right: '-16%', paletteIndex: 1 },
];

const REDUCED_MOTION_SET_3_MOBILE: OrbConfigItem[] = [
  { size: 240, top: '-10%', left: '-12%', paletteIndex: 0 },
  { size: 220, top: '10%', right: '-16%', paletteIndex: 1 },
  { size: 240, bottom: '-16%', left: '-8%', paletteIndex: 2 },
];

const REDUCED_MOTION_SET_4_MOBILE: OrbConfigItem[] = [
  { size: 240, top: '-10%', left: '-12%', paletteIndex: 0 },
  { size: 220, top: '10%', right: '-16%', paletteIndex: 1 },
  { size: 240, bottom: '-16%', left: '4%', paletteIndex: 2 },
  { size: 220, bottom: '-12%', right: '-12%', paletteIndex: 3 },
];

// Contrasting static colors for reduced motion - distinct per orb
const REDUCED_MOTION_COLORS = [
  [59, 130, 246],   // Blue
  [168, 85, 247],   // Purple
  [236, 72, 153],   // Pink
  [34, 211, 238],   // Cyan
];

// Linear interpolation between two RGB colors (t 0–1)
function lerpRgb(a: RGB, b: RGB, t: number): RGB {
  return [
    Math.round(a[0] + (b[0] - a[0]) * t),
    Math.round(a[1] + (b[1] - a[1]) * t),
    Math.round(a[2] + (b[2] - a[2]) * t),
  ];
}

// Build a morph gradient with smooth center: extra interpolated stops so the middle blends naturally
function gradientForMinuteIndex(paletteIndex: number, minuteIndex: number): string {
  const themeIndex = (paletteIndex + minuteIndex) % MORPH_THEMES.length;
  const theme = MORPH_THEMES[themeIndex];
  // Interpolate between theme colors for more stops = softer, more natural center (no visible bands)
  const colors: RGB[] = [];
  for (let i = 0; i < theme.length - 1; i++) {
    colors.push(theme[i]);
    colors.push(lerpRgb(theme[i], theme[i + 1], 0.5));
  }
  colors.push(theme[theme.length - 1]);
  const stopStep = 65 / Math.max(colors.length - 1, 1);
  const parts = colors.map(([r, g, b], i) => {
    const pct = Math.min(65, Math.round(i * stopStep));
    return `rgb(${r}, ${g}, ${b}) ${pct}%`;
  });
  return `radial-gradient(circle, ${parts.join(', ')}, transparent 70%)`;
}

type OrbConfig = OrbConfigItem;

interface GradientOrbProps {
  config: OrbConfig;
  index: number;
  minuteIndex: number;
  isInCycle: boolean;
  isMobile: boolean;
  prefersReducedMotion: boolean;
  isDarkMode: boolean;
  isVisible: boolean;
}

/** Static orb; isInCycle controls appear/exit (opacity). Color crossfades when minuteIndex changes. */
const GradientOrb = React.memo(function GradientOrb({
  config,
  index,
  minuteIndex,
  isInCycle,
  isMobile,
  prefersReducedMotion,
  isDarkMode,
  isVisible,
}: GradientOrbProps) {
  const positionStyles: React.CSSProperties = {
    width: config.size,
    height: config.size,
    minWidth: config.size,
    minHeight: config.size,
    borderRadius: '50%',
    top: config.top,
    left: config.left,
    right: config.right,
    bottom: config.bottom,
  };

  const blur = prefersReducedMotion
    ? (isMobile ? 22 : 32)
    : isDarkMode
      ? (isMobile ? 40 : 60)
      : (isMobile ? 25 : 35);

  const baseOpacity = prefersReducedMotion
    ? (isDarkMode ? 0.6 : 0.72)
    : isDarkMode
      ? (isMobile ? 0.5 : 0.6)
      : (isMobile ? 0.75 : 0.8);

  const opacity = baseOpacity + (index % 3) * (prefersReducedMotion ? 0.07 : 0.1);

  const gradient = gradientForMinuteIndex(config.paletteIndex, minuteIndex);
  const staticGradient = `radial-gradient(circle, rgb(${REDUCED_MOTION_COLORS[index % REDUCED_MOTION_COLORS.length].join(',')}) 0%, transparent 70%)`;

  return (
    <motion.div
      className="absolute rounded-full overflow-hidden"
      style={{
        ...positionStyles,
        filter: `blur(${blur}px)`,
        transform: 'translateZ(0)',
      }}
      initial={false}
      animate={{
        opacity: isInCycle ? opacity : 0,
        scale: isInCycle ? 1 : 0.85,
      }}
      transition={{ duration: 1.2, ease: 'easeInOut' }}
    >
      {prefersReducedMotion ? (
        <div
          className="absolute inset-0 rounded-full"
          style={{ background: staticGradient }}
        />
      ) : (
        <AnimatePresence mode="wait">
          <motion.div
            key={minuteIndex}
            className="absolute inset-0 rounded-full"
            style={{ background: gradient }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 2.2, ease: 'easeInOut' }}
          />
        </AnimatePresence>
      )}
    </motion.div>
  );
});

/** Always return the 4-orb set; visibility is controlled by isInCycle (index < orbCount). */
function getFourOrbSet(isMobile: boolean, prefersReducedMotion: boolean): OrbConfigItem[] {
  if (prefersReducedMotion) {
    return isMobile ? REDUCED_MOTION_SET_4_MOBILE : REDUCED_MOTION_SET_4;
  }
  return isMobile ? ORB_SET_4_MOBILE : ORB_SET_4;
}

export const GradientOrbs = React.memo(function GradientOrbs() {
  const isVisible = usePageVisibility();
  const [isMobile, setIsMobile] = useState(false);
  const [orbCycleIndex, setOrbCycleIndex] = useState(0);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => document.documentElement.classList.contains('dark'));
  const [minuteIndex, setMinuteIndex] = useState(() => Math.floor(Date.now() / COLOR_UPDATE_INTERVAL_MS));

  const orbCount = ORB_COUNT_SEQUENCE[orbCycleIndex];

  useEffect(() => {
    setIsMobile(window.innerWidth < 768);
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(motionQuery.matches);
    const handleMotionChange = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    motionQuery.addEventListener('change', handleMotionChange);
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => {
      motionQuery.removeEventListener('change', handleMotionChange);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // Cycle orb count: 2 → 3 → 4 → 2 → 4 → 3 → 4 → 3 → 2 (all screens)
  useEffect(() => {
    const id = setInterval(() => {
      setOrbCycleIndex((i) => (i + 1) % ORB_COUNT_SEQUENCE.length);
    }, ORB_COUNT_CYCLE_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  // Update color once per minute (no RAF) for energy savings
  useEffect(() => {
    const id = setInterval(() => {
      setMinuteIndex(Math.floor(Date.now() / COLOR_UPDATE_INTERVAL_MS));
    }, COLOR_UPDATE_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  const orbs = getFourOrbSet(isMobile, prefersReducedMotion);

  return (
    <div
      className="absolute inset-0 overflow-hidden pointer-events-none"
      style={{
        transform: 'translateZ(0)',
        willChange: 'transform',
      }}
    >
      {orbs.map((config, index) => (
        <GradientOrb
          key={index}
          config={config}
          index={index}
          minuteIndex={minuteIndex}
          isInCycle={index < orbCount}
          isMobile={isMobile}
          prefersReducedMotion={prefersReducedMotion}
          isDarkMode={isDarkMode}
          isVisible={isVisible}
        />
      ))}
    </div>
  );
});
